import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { Box, Grid, Typography } from '@material-ui/core';
import queryParams from '../../../../../common/service/queryParams';
import { UrlParamTypes } from '../../../../../common/enum/UrlParamTypes';
import collectedFilters from '../../../../../common/service/collectedFilters';
import useCheckoutContext from '../../../../common/hooks/useCheckoutContext';
import Translation from '../../../../../common/components/presentation/Translation/Translation';
import Item from './item/Item';
import expressionsOfInterestStyles from './expressionsOfInterestStyles';
import { EventDataTypes } from '../../../../../gql/common/enum/DataLayerEnums';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import { DxpDataLayerEventNames } from '../../../../../../../common/utilities/customDatalayerInterface';
import useNewDxpDigitalAssignmentEntryEvent from '../../../../../gql/common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import {
  getCheckoutStepsCommonFields,
  getEventDataCommonFields,
  getPurchaseOptionCommonFields,
  vproductCommonFields,
  removeHostNameFromUrl,
} from '../../../../../gql/common/utils/dxpDataLayerCommonFields';
import useJourneyStepsContext from '../../../../../gql/checkout4/hooks/useJourneyStepsContext';

export interface ExpressionsOfInterestProps {
  dealExpressionsOfInterest: string[];
}

const ExpressionsOfInterest: FunctionComponent<ExpressionsOfInterestProps> = ({ dealExpressionsOfInterest }) => {
  const history = useHistory();
  const {
    filters: { expressionsOfInterest: eoi },
  } = collectedFilters();
  const { expressionOfInterest, deal } = useCheckoutContext();
  const eoiFilters = eoi?.split(',');
  const selectedItems = useMemo(() => eoiFilters || [], [eoiFilters]);
  const styles = expressionsOfInterestStyles();
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();
  const {
    platformConfig: { ga4DxpDataLayer },
  } = usePlatformConfig();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};

  const memoizedSelectedItems = useMemo(() => selectedItems, [selectedItems]);
  const isSelected = useCallback((code: string) => memoizedSelectedItems.includes(code), [memoizedSelectedItems]);
  const { steps, activeStep } = useJourneyStepsContext();

  const getProductLabelFromCode = useCallback((code: string): string => {
    const label = expressionOfInterest.find(item => item?.code === code);
    return label?.attributes?.label ?? '';
  }, [expressionOfInterest]);

  const handleSelectedItem = useCallback(
    (code: string) => {
      const selectedCodes =
        selectedItems && selectedItems?.includes(code)
          ? selectedItems.filter(item => item !== code)
          : [...selectedItems, code];

      const productLabel = getProductLabelFromCode(code);
      const eoiCtaTextName = isSelected(code)
        ? EventDataTypes.EOI_REMOVE_BUTTON_TEXT
        : EventDataTypes.EOI_ADD_BUTTON_TEXT;

      if (deal && steps && activeStep) {
        callDxpDigitalAssignmentEntryEvent(
          {
            event_action: removeHostNameFromUrl(window.location.href),
            event_label: `${EventDataTypes.BUTTON}::${eoiCtaTextName}`,
            event_category: EventDataTypes.CHECKOUT_CLICK,
            event_pagecategory: EventDataTypes.CHECKOUT,
            click_url: removeHostNameFromUrl(window.location.href),
            click_text: eoiCtaTextName,
            click_name: productLabel.toLowerCase(),
            ...getEventDataCommonFields(DxpDataLayerEventNames.PRODUCTS_CTA_CLICK, true),
            purchaseOption: {
              vehicle_cash_price: deal.car?.prices?.carPrice?.originalPrice?.[0]?.money?.amount?.toString() ?? '',
              terms: deal.finance?.formInput?.term?.toString() ?? '',
              monthly_mileage: deal.finance?.formInput?.mileage?.toString() ?? '',
              reservation_fee: deal.attributes?.paymentFee?.reserveFee?.[0]?.amount?.toString() ?? '',
              ...getPurchaseOptionCommonFields(deal),
            },
            vproduct: {
              est_delivery_time: deal.car?.deliveryTimeFrame?.toString() ?? '',
              total_weight: '',
              max_speed: '',
              ...vproductCommonFields(deal),
            },
            checkoutStep: {
              ...getCheckoutStepsCommonFields(steps, activeStep),
            },
          },
          jlrDataLayer,
        );
      }

      queryParams.changeArrayValue(history.location, history, UrlParamTypes.EXPRESSIONS_OF_INTEREST, selectedCodes);
    },
    [location, selectedItems, callDxpDigitalAssignmentEntryEvent, deal, jlrDataLayer, steps, activeStep, isSelected],
  );

  useEffect(() => {
    if (selectedItems?.length === 1 && selectedItems[0] === '') {
      queryParams.removeParam(history.location, history, UrlParamTypes.EXPRESSIONS_OF_INTEREST);
    }
  }, [selectedItems]);

  useEffect(() => {
    if (!selectedItems.length && dealExpressionsOfInterest.length) {
      queryParams.changeArrayValue(
        history.location,
        history,
        UrlParamTypes.EXPRESSIONS_OF_INTEREST,
        dealExpressionsOfInterest,
      );
    }
  }, [dealExpressionsOfInterest]);

  if (!expressionOfInterest?.length) {
    return null;
  }

  return (
    <Box className={styles.container} data-test={'checkout:extras:expression_of_interest:container'}>
      <Typography variant={'h4'} data-test={'checkout:extras:expression_of_interest:title'}>
        <Translation id={'trans__checkout__extras_page__eoi_title'} className={styles.title} />
      </Typography>
      <Typography data-test={'checkout:extras:expression_of_interest:description'}>
        <Translation id={'trans__checkout__extras_page__eoi_description'} className={styles.description} />
      </Typography>
      <Grid
        container
        spacing={1}
        className={styles.itemsWrapper}
        data-test={'checkout:extras:expression_of_interest:items'}
      >
        {expressionOfInterest?.length &&
          expressionOfInterest.map(item => (
            <Item
              item={item}
              isSelected={selectedItems?.includes(item.code)}
              handleSelectedItem={handleSelectedItem}
              key={item.code}
            />
          ))}
      </Grid>
    </Box>
  );
};

export default ExpressionsOfInterest;
